import { Alert, Badge, Divider, Group, Stack, Text } from '@mantine/core';
import type { GetWorkOrderResponseDto } from '@/generated/api/pacApi.schemas.ts';
import WorkOrderSalesOrderLink from '@/features/work-orders/components/detail-tabs/WorkOrderSalesOrderLink.tsx';

// eslint-disable-next-line react-refresh/only-export-components
export const formatCurrency = (
  amount: number,
  locale = 'en-US',
  currency = 'USD',
) =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
  }).format(amount);

function formatSignedCurrency(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    signDisplay: 'exceptZero',
  }).format(amount);
}

function roundCurrency(amount: number) {
  return Math.round(amount * 100) / 100;
}

function formatPercentage(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value);
}

function CalculationRow({ label, amount }: { label: string; amount: number }) {
  return (
    <Group justify="space-between" gap="md" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text
        size="sm"
        fw={500}
        style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {formatSignedCurrency(amount)}
      </Text>
    </Group>
  );
}

function GrossProfitRow({
  label,
  revenue,
  cost,
  grossProfit,
  costLabel,
}: {
  label: string;
  revenue: number;
  cost: number;
  grossProfit: number;
  costLabel: string;
}) {
  const grossMargin = revenue === 0 ? null : grossProfit / revenue;

  return (
    <Group justify="space-between" align="flex-start" gap="md" wrap="nowrap">
      <Stack gap={0}>
        <Text size="sm" c="dimmed">
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {formatCurrency(revenue)} revenue − {formatCurrency(cost)} {costLabel}
        </Text>
      </Stack>
      <Text
        size="sm"
        fw={700}
        c={grossProfit < 0 ? 'red' : undefined}
        style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
      >
        {formatCurrency(grossProfit)}
        {grossMargin === null ? null : ` (${formatPercentage(grossMargin)})`}
      </Text>
    </Group>
  );
}

export default function WorkOrderDetailsFinanceSection({
  workOrder,
}: {
  workOrder: GetWorkOrderResponseDto;
}) {
  const pay = workOrder.pay;
  const isLegacyAmount =
    pay.amountIHaveToPay !== null &&
    !pay.isSettled &&
    pay.buyerProjectedTotalMaxAmount === null;
  const calculation = [
    ['Labor', pay.payLaborAmount],
    ['Bonus', pay.payBonusAmount],
    ['Penalty', pay.payPenaltyAmount],
    ['Approved expenses', pay.payApprovedExpenseAmount],
    ['Discount', pay.payDiscountAmount],
    ['Fees and tax', pay.payBuyerFeesAmount],
    ['Cancellation charge', pay.payCancellationFeeAmount],
  ].filter(
    (entry): entry is [string, number] => entry[1] !== null && entry[1] !== 0,
  );
  const calculationTotal = roundCurrency(
    calculation.reduce((total, [, amount]) => total + amount, 0),
  );
  const reconciliation =
    pay.amountIHaveToPay === null
      ? 0
      : roundCurrency(pay.amountIHaveToPay - calculationTotal);
  const pendingItems = [
    pay.payHasPendingExpenses ? 'expenses' : null,
    pay.payHasPendingIncrease ? 'pay increase' : null,
    pay.payHasPendingCancellation ? 'cancellation charge' : null,
  ].filter((item): item is string => item !== null);
  const amountStatus =
    pay.amountIHaveToPay === null
      ? 'Unavailable'
      : pay.isSettled
        ? 'Settled'
        : 'Projected';
  const hasSettledPayment = pay.buyerSettledTotalAmount !== null;
  const grossProfitRows = [
    pay.budgetedRevenue !== null &&
    pay.budgetedGrossProfit !== null &&
    pay.amountIHaveToPay !== null
      ? {
          label: 'Budgeted gross profit',
          revenue: pay.budgetedRevenue,
          grossProfit: pay.budgetedGrossProfit,
          cost: pay.amountIHaveToPay,
        }
      : null,
    pay.actualRevenue !== null &&
    pay.actualGrossProfit !== null &&
    pay.amountIHaveToPay !== null
      ? {
          label: hasSettledPayment
            ? 'Actual gross profit'
            : 'Estimated actual gross profit',
          revenue: pay.actualRevenue,
          grossProfit: pay.actualGrossProfit,
          cost: pay.amountIHaveToPay,
        }
      : null,
  ].filter(
    (
      row,
    ): row is {
      label: string;
      revenue: number;
      grossProfit: number;
      cost: number;
    } => row !== null,
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2}>
          <Text size="xs" c="dimmed">
            Expected amount owed
          </Text>
          <Text
            size="xl"
            fw={750}
            c={pay.amountIHaveToPay === null ? 'dimmed' : undefined}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {pay.amountIHaveToPay === null
              ? 'Not available'
              : formatCurrency(pay.amountIHaveToPay)}
          </Text>
        </Stack>
        <Badge
          size="sm"
          variant="light"
          color={
            amountStatus === 'Settled'
              ? 'green'
              : isLegacyAmount
                ? 'gray'
                : 'blue'
          }
        >
          {amountStatus}
        </Badge>
      </Group>

      {calculation.length > 0 ? (
        <>
          <Divider />
          <Stack gap="xs">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              Calculation
            </Text>
            {calculation.map(([label, amount]) => (
              <CalculationRow key={label} label={label} amount={amount} />
            ))}
            {reconciliation !== 0 ? (
              <CalculationRow
                label={
                  pay.isSettled
                    ? 'Settlement adjustment'
                    : 'Projection adjustment'
                }
                amount={reconciliation}
              />
            ) : null}
            <Divider variant="dashed" />
            <Group justify="space-between" gap="md" wrap="nowrap">
              <Text size="sm" fw={700}>
                Amount owed
              </Text>
              <Text
                size="sm"
                fw={700}
                style={{
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                }}
              >
                {pay.amountIHaveToPay === null
                  ? 'Not available'
                  : formatCurrency(pay.amountIHaveToPay)}
              </Text>
            </Group>
          </Stack>
        </>
      ) : null}

      {pendingItems.length > 0 ? (
        <Alert color="yellow" variant="light" title="Amount may change">
          Pending: {pendingItems.join(', ')}.
        </Alert>
      ) : null}

      {grossProfitRows.length > 0 ? (
        <>
          <Divider />
          <Stack gap="xs">
            <Text size="xs" fw={700} c="dimmed" tt="uppercase">
              Gross profit
            </Text>
            {grossProfitRows.map((row) => (
              <GrossProfitRow
                key={row.label}
                {...row}
                costLabel={hasSettledPayment ? 'settled cost' : 'expected cost'}
              />
            ))}
          </Stack>
        </>
      ) : null}

      <Divider />
      <Group justify="space-between" gap="md" wrap="nowrap">
        <Text size="sm" c="dimmed">
          Sales Order
        </Text>
        <WorkOrderSalesOrderLink salesOrderExternalId={workOrder.salesOrder} />
      </Group>
    </Stack>
  );
}
